/* ============================================================================
   Coldest Bundle Auto-Apply
   ----------------------------------------------------------------------------
   Watches the cart for drop bundle eligibility and silently applies the
   matching BUNDLE-{HANDLE}-10 discount code via the /discount/CODE endpoint.

   How it works:
     1. On page load and any cart-change event, fetch /cart.js
     2. For each cart line item, look up which drop collection (if any) the
        item's variant belongs to. The mapping comes from
        window.__coldestDropVariants which is built server-side via a small
        Liquid script that reads each drop collection's variants.
     3. Tally items per drop collection. If any drop has 3+ items in cart,
        apply that drop's BUNDLE-{HANDLE}-10 code via fetch.
     4. Track the last-applied code in sessionStorage so we don't refetch
        unnecessarily on every cart change.

   This script is fully passive — it never blocks navigation or shows UI.
   Any Shopify Admin cart-watcher / drawer that re-renders on cart:updated
   will pick up the discount on its next refresh.
============================================================================ */

(function () {
  'use strict';

  var STORAGE_KEY = 'coldest_bundle_applied_code';
  var DEBOUNCE_MS = 600;

  function log() {
    if (window.location.search.indexOf('debug=1') !== -1) {
      try { console.log.apply(console, ['[bundle-auto]'].concat([].slice.call(arguments))); } catch (e) {}
    }
  }

  /* Get the variant→drop-handle map. Built server-side by Liquid and
     attached to window before this script runs. Shape:
       { "47675444265192": "irises", "47675444330728": "irises", ... } */
  function getVariantMap() {
    return (window.__coldestDropVariants && typeof window.__coldestDropVariants === 'object')
      ? window.__coldestDropVariants
      : null;
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  /* Tally cart items per drop handle. Returns { handle: count } */
  function tallyByDrop(cart, variantMap) {
    var tally = {};
    if (!cart || !cart.items) return tally;
    cart.items.forEach(function (line) {
      var vid = String(line.variant_id || line.id);
      var dropHandle = variantMap[vid];
      if (!dropHandle) return;
      tally[dropHandle] = (tally[dropHandle] || 0) + (line.quantity || 1);
    });
    return tally;
  }

  /* Pick the drop with the most items in cart that meets the 3+ threshold.
     If multiple drops qualify (rare), picks the one with the highest count. */
  function pickEligibleDrop(tally) {
    var best = null;
    var bestCount = 0;
    Object.keys(tally).forEach(function (handle) {
      if (tally[handle] >= 3 && tally[handle] > bestCount) {
        best = handle;
        bestCount = tally[handle];
      }
    });
    return best;
  }

  function applyDiscount(code) {
    /* Apply the discount cookie. Shopify's /discount/{CODE} endpoint sets the
       discount session cookie via Set-Cookie on a 302 response. We use a
       lightweight redirect:'follow' so the browser walks the redirect chain
       and applies cookies the same way a real navigation would. We don't care
       about the final response body — just the side effect.

       Falls back to a hidden iframe if fetch() fails (some CSP setups block
       cross-origin redirects from JS). The iframe form is bulletproof since
       it's just a normal page navigation in a sandbox. */
    var url = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart.js');
    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'follow'
    }).catch(function () {
      /* Fallback: hidden iframe to ensure cookie gets set */
      try {
        var f = document.createElement('iframe');
        f.style.display = 'none';
        f.src = url;
        f.onload = function () { setTimeout(function () { try { f.remove(); } catch (e) {} }, 1000); };
        document.body.appendChild(f);
      } catch (e) {}
    });
  }

  function clearStoredCode() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  function setStoredCode(code) {
    try { sessionStorage.setItem(STORAGE_KEY, code); } catch (e) {}
  }

  function getStoredCode() {
    try { return sessionStorage.getItem(STORAGE_KEY) || ''; } catch (e) { return ''; }
  }

  /* Main check: re-fetch cart, decide if a bundle code should be applied. */
  var inFlight = false;
  function check() {
    if (inFlight) return;
    var variantMap = getVariantMap();
    if (!variantMap) return; /* No map yet — abort silently */

    inFlight = true;
    fetchCart().then(function (cart) {
      inFlight = false;
      if (!cart) return;

      var tally = tallyByDrop(cart, variantMap);
      var eligible = pickEligibleDrop(tally);
      var stored = getStoredCode();

      if (eligible) {
        var code = 'BUNDLE-' + eligible.toUpperCase() + '-10';
        if (stored !== code) {
          log('applying', code, '— tally:', tally);
          applyDiscount(code).then(function () {
            setStoredCode(code);
            try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (e) {}
            try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (e) {}
          });
        } else {
          log('already applied', code);
        }
      } else if (stored && stored.indexOf('BUNDLE-') === 0) {
        /* Cart fell below threshold — Shopify auto-removes the discount on its
           own; we just clear our local memory so a re-qualifying cart re-applies. */
        log('cart no longer eligible, clearing stored code', stored);
        clearStoredCode();
      }
    });
  }

  /* Debounced wrapper: cart-update events can fire several times in quick
     succession (add multi-line bundle, etc) — coalesce them. */
  var debounceTimer = null;
  function checkDebounced() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(check, DEBOUNCE_MS);
  }

  /* Listen for cart events from any source: theme, MonsterCart, Rebuy, etc.
     We intentionally avoid monkey-patching window.fetch — cart drawers
     consistently dispatch one of these on every cart change. */
  ['cart:updated', 'cart:refresh', 'cart:change',
   'monstercart:updated', 'cartdrawer:open',
   'theme:cart:change', 'theme:cart:update'].forEach(function (ev) {
    document.addEventListener(ev, checkDebounced);
    window.addEventListener(ev, checkDebounced);
  });

  /* Also re-check whenever the page becomes visible again (handles back/forward
     cache and tab-switch scenarios where cart was modified elsewhere). */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') checkDebounced();
  });

  /* Initial check on load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', check);
  } else {
    check();
  }
})();
